<template>
  <div class="update-works-container">
    <!-- Header Section -->
 

    <!-- Tabs Navigation -->
    <ul class="nav nav-tabs modern-tabs" role="tablist">
      <li class="nav-item">
        <a class="nav-link active" data-bs-toggle="tab" href="#assignedWork" role="tab">
          <i class="bi bi-clipboard-check me-2"></i>
          <span>Assigned Work</span>
          <span v-if="assignedWorks.length > 0" class="badge-count">{{ assignedWorks.length }}</span>
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-bs-toggle="tab" href="#kytUpdates" role="tab">
          <i class="bi bi-calendar-event me-2"></i>
          <span>KYT Updates</span>
          <span v-if="paginationInfo.totalOverdueKyts > 0" class="badge-count">{{ paginationInfo.totalOverdueKyts }}</span>
        </a>
      </li>
    </ul>

    <!-- Tabs Content -->
    <div class="tab-content modern-tab-content">
      <!-- Assigned Work Tab -->
      <div class="tab-pane fade show active" id="assignedWork" role="tabpanel">
        <div class="content-card">
          <div v-if="isLoadingWorks" class="text-center py-5">
            <div class="spinner-border spinner-border-lg text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading work assignments...</p>
          </div>
          
          <div v-else>
            <div v-if="assignedWorks.length === 0" class="empty-state">
              <i class="bi bi-inbox empty-icon"></i>
              <h4>No Pending Work Assignments</h4>
              <p class="text-muted">You're all caught up! There are no pending work assignments at the moment.</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table modern-table">
                <thead>
                  <tr>
                    <th><i class="bi bi-briefcase me-2"></i>Work Title</th>
                    <th><i class="bi bi-building me-2"></i>Site Name</th>
                    <th><i class="bi bi-calendar3 me-2"></i>Assignment Date</th>
                    <th><i class="bi bi-flag me-2"></i>Priority</th>
                    <th><i class="bi bi-check-circle me-2"></i>Status</th>
                    <th><i class="bi bi-gear me-2"></i>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="work in assignedWorks" :key="work.id" class="table-row-hover">
                    <td class="fw-semibold">{{ work.workTitle }}</td>
                    <td>{{ work.siteName }}</td>
                    <td>
                      <span class="date-badge">
                        <i class="bi bi-calendar-date me-1"></i>
                        {{ work.currentDate }}
                      </span>
                    </td>
                    <td>
                      <span :class="['priority-badge', `priority-${work.priority.toLowerCase()}`]">
                        <i :class="getPriorityIcon(work.priority)"></i>
                        {{ work.priority }}
                      </span>
                    </td>
                    <td>
                      <span :class="['status-badge', `status-${work.status.toLowerCase()}`]">
                        <i :class="getStatusIcon(work.status)"></i>
                        {{ work.status }}
                      </span>
                    </td>
                    <td>
                      <button 
                        class="btn btn-action" 
                        @click="openChangeDateModal('work', work)"
                        :disabled="work.status.toLowerCase() === 'completed'"
                      >
                        <i class="bi bi-calendar-plus me-1"></i>
                        Change Date
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- KYT Updates Tab -->
      <div class="tab-pane fade" id="kytUpdates" role="tabpanel">
        <div class="content-card">
          <!-- Pagination and Sorting Controls for KYT -->
          <div class="controls-section">
            <div class="controls-wrapper">
              <div class="control-group">
                <label class="control-label">
                  <i class="bi bi-list-ul me-2"></i>
                  Items per page:
                </label>
                <select class="form-select modern-select" v-model="kytPagination.limit" @change="fetchKytData">
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
              <div class="control-group">
                <label class="control-label">
                  <i class="bi bi-sort-down me-2"></i>
                  Sort Order:
                </label>
                <select class="form-select modern-select" v-model="kytPagination.sortOrder" @change="fetchKytData">
                  <option value="asc">
                    <i class="bi bi-sort-up"></i>
                    Ascending
                  </option>
                  <option value="desc">
                    <i class="bi bi-sort-down"></i>
                    Descending
                  </option>
                </select>
              </div>
            </div>
          </div>
          
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border spinner-border-lg text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading KYT updates...</p>
          </div>
          
          <div v-else>
            <div v-if="kytUpdates.length === 0" class="empty-state">
              <i class="bi bi-calendar-x empty-icon"></i>
              <h4>No KYT Updates Found</h4>
              <p class="text-muted">There are no KYT updates available for the selected criteria.</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table modern-table">
                <thead>
                  <tr>
                    <th><i class="bi bi-phone me-2"></i>KYT Type</th>
                    <th><i class="bi bi-building me-2"></i>Site Name</th>
                    <th><i class="bi bi-clock me-2"></i>Current NFD</th>
                    <th><i class="bi bi-flag me-2"></i>Priority</th>
                    <th><i class="bi bi-check-circle me-2"></i>Status</th>
                    <th><i class="bi bi-gear me-2"></i>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="kyt in kytUpdates" :key="kyt.id" class="table-row-hover">
                    <td>
                      <span :class="['type-badge', `type-${kyt.type.toLowerCase()}`]">
                        <i :class="getKytTypeIcon(kyt.type)"></i>
                        {{ kyt.type }}
                      </span>
                    </td>
                    <td class="fw-semibold">{{ kyt.siteName }}</td>
                    <td>
                      <span class="date-badge">
                        <i class="bi bi-alarm me-1"></i>
                        {{ kyt.nfd }}
                      </span>
                    </td>
                    <td>
                      <span :class="['priority-badge', `priority-${kyt.priority.toLowerCase()}`]">
                        <i :class="getPriorityIcon(kyt.priority)"></i>
                        {{ kyt.priority }}
                      </span>
                    </td>
                    <td>
                      <span :class="['status-badge', `status-${kyt.status.toLowerCase()}`]">
                        <i :class="getStatusIcon(kyt.status)"></i>
                        {{ kyt.status }}
                      </span>
                    </td>
                    <td>
                      <button 
                        class="btn btn-action" 
                        @click="openChangeDateModal('kyt', kyt)"
                        :disabled="kyt.status === 'Completed'"
                      >
                        <i class="bi bi-calendar-plus me-1"></i>
                        Change NFD
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination for KYT -->
            <div v-if="kytUpdates.length > 0" class="pagination-section">
              <div class="pagination-wrapper">
                <div class="pagination-info-section">
                  <i class="bi bi-info-circle me-2"></i>
                  <span class="pagination-text">
                    Page <strong>{{ paginationInfo.currentPage }}</strong> of <strong>{{ paginationInfo.totalPages }}</strong>
                  </span>
                  <span class="pagination-divider">•</span>
                  <span class="pagination-total">
                    <strong>{{ paginationInfo.totalOverdueKyts }}</strong> total items
                  </span>
                </div>
                <div class="pagination-controls-section">
                  <button 
                    class="btn btn-pagination" 
                    @click="changeKytPage(kytPagination.page - 1)"
                    :disabled="!paginationInfo.hasPrevPage || isLoading"
                  >
                    <i class="bi bi-chevron-left"></i>
                    Previous
                  </button>
                  <div class="page-input-group">
                    <input 
                      type="number" 
                      class="form-control page-input" 
                      v-model.number="kytPagination.page"
                      @change="fetchKytData"
                      min="1"
                      :max="paginationInfo.totalPages"
                      placeholder="Page"
                    />
                  </div>
                  <button 
                    class="btn btn-pagination" 
                    @click="changeKytPage(kytPagination.page + 1)"
                    :disabled="!paginationInfo.hasNextPage || isLoading"
                  >
                    Next
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Change Date Modal -->
    <div class="modal fade" id="changeDateModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content modern-modal">
          <div class="modal-header">
            <h5 class="modal-title">
              <i :class="selectedType === 'kyt' ? 'bi bi-calendar-event' : 'bi bi-calendar3'" class="me-2"></i>
              {{ selectedType === 'kyt' ? 'Change NFD Date & Time' : 'Change Assignment Date' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form-group mb-3">
              <label class="form-label">
                <i class="bi bi-calendar-date me-2"></i>
                New Date
              </label>
              <input type="date" class="form-control modern-input" v-model="newDate">
            </div>
            <!-- Only show time input for KYT updates -->
            <div v-if="selectedType === 'kyt'" class="form-group mb-3">
              <label class="form-label">
                <i class="bi bi-clock me-2"></i>
                Time
              </label>
              <input type="time" class="form-control modern-input" v-model="newTime">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-cancel" data-bs-dismiss="modal">
              <i class="bi bi-x-circle me-1"></i>
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-submit" 
              @click="submitDateChange"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check-circle me-1"></i>
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { fetchGetApi, fetchPostApi } from '@/util/api'
import { Modal } from 'bootstrap'
import { useToast } from 'vue-toast-notification'

const toast = useToast()
const route = useRoute()
const empId = route.params.employeeId

// Pagination state for KYT only
const kytPagination = ref({
  limit: 20,
  page: 1,
  sortOrder: 'desc'
})

// Pagination info from API
const paginationInfo = ref({
  currentPage: 1,
  totalPages: 1,
  totalOverdueKyts: 0,
  hasNextPage: false,
  hasPrevPage: false
})

// Replace dummy assignedWorks with actual data state
const assignedWorks = ref([])
const isLoadingWorks = ref(false)

// Add function to fetch work assignments
const fetchWorkAssignments = async () => {
  isLoadingWorks.value = true
  try {
    const response = await fetchGetApi(`api/work/assignment/employee/pending/${empId}`)
    console.log('Work assignments response:', response) // Debug log
    
    // Check if response has data property and it's an array
    if (response?.data?.data) { // Access nested data property
      // Transform API response to match our table structure
      assignedWorks.value = response.data.data.map(work => {
        console.log('Processing work item:', work) // Debug individual items
        // Use assignmentDate if available, otherwise fall back to assignedDate
        const dateToDisplay = work.assignmentDate || work.assignedDate
        return {
          id: work._id,
          workTitle: work.workTitle || 'N/A',
          siteName: work.siteName || 'N/A',
          currentDate: formatAssignmentDate(dateToDisplay),
          priority: work.priority ? work.priority.charAt(0).toUpperCase() + work.priority.slice(1).toLowerCase() : 'Medium',
          status: work.status ? work.status.charAt(0).toUpperCase() + work.status.slice(1).toLowerCase() : 'Pending',
          originalData: work
        }
      })
      console.log('Transformed work assignments:', assignedWorks.value) // Debug transformed data
    } else {
      console.warn('No work assignments found or invalid response format')
      assignedWorks.value = []
    }
  } catch (error) {
    console.error('Error fetching work assignments:', error)
    toast.error('Failed to fetch work assignments')
    assignedWorks.value = []
  } finally {
    isLoadingWorks.value = false
  }
}

// Update the formatAssignmentDate function to handle different date formats
const formatAssignmentDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid Date'
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'N/A'
  }
}

// KYT data state
const kytUpdates = ref([])
const isLoading = ref(false)

// Fetch KYT data
const fetchKytData = async () => {
  isLoading.value = true
  try {
    const response = await fetchGetApi(`api/kyt/getByEmpId/${empId}?limit=${kytPagination.value.limit}&page=${kytPagination.value.page}&sortOrder=${kytPagination.value.sortOrder}`)
    console.log('Raw API response:', response)
    
    // Check if response is nested in a data property
    const responseData = response.data || response
    
    if (!responseData || !responseData.data || !Array.isArray(responseData.data)) {
      console.error('Invalid response format')
      kytUpdates.value = []
      return
    }
    
    // Update pagination info from API response
    if (responseData.pagination) {
      paginationInfo.value = {
        currentPage: responseData.pagination.currentPage,
        totalPages: responseData.pagination.totalPages,
        totalOverdueKyts: responseData.pagination.totalOverdueKyts,
        hasNextPage: responseData.pagination.hasNextPage,
        hasPrevPage: responseData.pagination.hasPrevPage
      }
    }
    
    // Transform API response to match our table structure
    kytUpdates.value = responseData.data.map(kyt => {
      const latestFollowUp = kyt.kytData?.followUps?.[0] || {}
      
      return {
        id: kyt._id,
        type: getKytType(latestFollowUp),
        siteName: kyt.siteId?.siteName || 'N/A',
        nfd: kyt.NFD || 'N/A',
        priority: getPriorityFromRating(latestFollowUp.rating),
        status: getKytStatus(kyt),
        originalData: kyt
      }
    })
  } catch (error) {
    console.error('Error fetching KYT data:', error)
    kytUpdates.value = []
  } finally {
    isLoading.value = false
  }
}

// Helper function to determine KYT type
const getKytType = (followUp) => {
  if (!followUp) return 'N/A'
  if (followUp.isCall) return 'Call'
  if (followUp.isVisit) return 'Visit'
  return 'Other'
}

// Helper function to format NFD date
const formatNFD = (nfdString) => {
  if (!nfdString) return 'N/A'
  // NFD format is already "DD-MM-YYYY – HH:mm AM/PM"
  return nfdString
}

// Helper function to determine KYT status
const getKytStatus = (kyt) => {
  if (kyt.isCompleted) return 'Completed'
  if (kyt.isRequestForClose) return 'Pending Closure'
  return 'Pending'
}

// Updated priority rating function
const getPriorityFromRating = (rating) => {
  if (!rating) return 'Medium'
  if (rating >= 70) return 'High'
  if (rating >= 40) return 'Medium'
  return 'Low'
}

// Modal state
const newDate = ref('')
const newTime = ref('')
const selectedItem = ref(null)
const selectedType = ref(null)
const isSubmitting = ref(false)

// Modal functions
const openChangeDateModal = (type, item) => {
  selectedType.value = type
  selectedItem.value = item
  
  if (type === 'kyt') {
    // Parse the NFD format "DD-MM-YYYY – HH:mm AM/PM"
    const nfdParts = item.nfd.split('–').map(part => part.trim())
    const datePart = nfdParts[0] // DD-MM-YYYY
    let timePart = nfdParts[1] || '00:00 AM' // HH:mm AM/PM
    
    // Convert date to YYYY-MM-DD for input
    const [day, month, year] = datePart.split('-')
    newDate.value = `${year}-${month}-${day}`
    
    // Convert 12-hour time to 24-hour for input
    const timeMatch = timePart.match(/(\d{1,2}):(\d{2}) (AM|PM)/)
    if (timeMatch) {
      let [_, hours, minutes, period] = timeMatch
      hours = parseInt(hours)
      if (period === 'PM' && hours < 12) hours += 12
      if (period === 'AM' && hours === 12) hours = 0
      newTime.value = `${hours.toString().padStart(2, '0')}:${minutes}`
    } else {
      newTime.value = '00:00'
    }
  } else {
    // For work assignments, use assignmentDate if available, otherwise assignedDate
    const dateToUse = item.originalData.assignmentDate || item.originalData.assignedDate
    if (dateToUse) {
      // Convert ISO date to YYYY-MM-DD
      const date = new Date(dateToUse)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      newDate.value = `${year}-${month}-${day}`
    } else {
      // Fallback: try to parse from displayed date
      const [displayDay, displayMonth, displayYear] = item.currentDate.split('/')
      newDate.value = `${displayYear}-${displayMonth}-${displayDay}`
    }
    newTime.value = '00:00' // Not used for work assignments
  }
  
  const modalEl = document.getElementById('changeDateModal')
  const modal = new Modal(modalEl)
  modal.show()
}

const submitDateChange = async () => {
  if (selectedType.value === 'kyt') {
    try {
      isSubmitting.value = true
      
      // Convert date and time to required format
      const [year, month, day] = newDate.value.split('-')
      const [hours, minutes] = newTime.value.split(':')
      
      // Convert to 12-hour format
      let period = 'AM'
      let hours12 = parseInt(hours)
      if (hours12 >= 12) {
        period = 'PM'
        if (hours12 > 12) hours12 -= 12
      }
      if (hours12 === 0) hours12 = 12
      
      const formattedNFD = `${day}-${month}-${year} – ${hours12}:${minutes} ${period}`
      
      // Make the API call
      const response = await fetchPostApi('api/kyt/updateNFD', {
        kytId: selectedItem.value.id,
        newNFD: formattedNFD
      })
      
      console.log('API Response:', response) // Debug log
      
      // Check if response.data exists and has success property
      const responseData = response.data || response
      
      if (responseData && responseData.success) {
        toast.success(responseData.message)
        
        // Update the local state with the new NFD
        const updatedKyt = responseData.kyt
        const kytIndex = kytUpdates.value.findIndex(k => k.id === updatedKyt._id)
        if (kytIndex !== -1) {
          kytUpdates.value[kytIndex] = {
            ...kytUpdates.value[kytIndex],
            nfd: updatedKyt.NFD, // Use NFD from response
            status: getKytStatus(updatedKyt),
            originalData: updatedKyt
          }
        }
        
        // Close modal
        const modalEl = document.getElementById('changeDateModal')
        const modal = Modal.getInstance(modalEl)
        modal.hide()
      } else {
        // If response is not successful, throw error with message
        throw new Error(responseData?.message || 'Failed to update NFD')
      }
    } catch (error) {
      console.error('Error updating KYT NFD:', error)
      // Show the actual error message from the API if available
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update NFD'
      toast.error(errorMessage)
    } finally {
      isSubmitting.value = false
    }
  } else {
    try {
      isSubmitting.value = true
      
      // Convert date to required format (YYYY-MM-DD)
      const assignmentDate = newDate.value // Already in YYYY-MM-DD format from input
      
      // Make the API call
      const response = await fetchPostApi(`api/work/assignment/${selectedItem.value.id}/date`, {
        assignmentDate
      })
      
      console.log('Work assignment update response:', response)
      
      // Check if response exists and has success property
      const responseData = response.data || response
      
      if (responseData && responseData.success) {
        toast.success('Assignment date updated successfully')
        
        // Update the local state with the response data
        const updatedAssignment = responseData.data
        const workIndex = assignedWorks.value.findIndex(w => w.id === updatedAssignment._id)
        if (workIndex !== -1) {
          assignedWorks.value[workIndex] = {
            ...assignedWorks.value[workIndex],
            currentDate: formatAssignmentDate(updatedAssignment.assignmentDate),
            originalData: updatedAssignment
          }
        }
        
        // Close modal
        const modalEl = document.getElementById('changeDateModal')
        const modal = Modal.getInstance(modalEl)
        modal.hide()
        
        // Optionally refresh the work assignments
        await fetchWorkAssignments()
      } else {
        throw new Error(responseData?.message || 'Failed to update assignment date')
      }
    } catch (error) {
      console.error('Error updating assignment date:', error)
      toast.error(error.message || 'Failed to update assignment date')
    } finally {
      isSubmitting.value = false
    }
  }
}

// Pagination functions for KYT only
const changeKytPage = (newPage) => {
  if (newPage >= 1) {
    kytPagination.value.page = newPage
    fetchKytData()
  }
}

// Helper functions for icons
const getPriorityIcon = (priority) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'bi bi-exclamation-triangle-fill me-1'
    case 'medium':
      return 'bi bi-exclamation-circle-fill me-1'
    case 'low':
      return 'bi bi-info-circle-fill me-1'
    default:
      return 'bi bi-dash-circle-fill me-1'
  }
}

const getStatusIcon = (status) => {
  const statusLower = status.toLowerCase()
  if (statusLower === 'completed') return 'bi bi-check-circle-fill me-1'
  if (statusLower === 'pending') return 'bi bi-clock-fill me-1'
  if (statusLower.includes('progress')) return 'bi bi-arrow-repeat me-1'
  if (statusLower.includes('closure')) return 'bi bi-hourglass-split me-1'
  return 'bi bi-circle-fill me-1'
}

const getKytTypeIcon = (type) => {
  switch (type.toLowerCase()) {
    case 'call':
      return 'bi bi-telephone-fill me-1'
    case 'visit':
      return 'bi bi-geo-alt-fill me-1'
    default:
      return 'bi bi-question-circle-fill me-1'
  }
}

onMounted(() => {
  fetchKytData()
  fetchWorkAssignments()
})
</script>

<style scoped>
/* Modern Container */
.update-works-container {
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
}

/* Page Header */
.page-header {
  background: #3e5fce;
  padding: 32px;
  border-radius: 16px;
  color: white;
  box-shadow: 0 10px 40px rgba(62, 95, 206, 0.3);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  margin: 8px 0 0 0;
  font-size: 16px;
  opacity: 0.95;
}

/* Modern Tabs */
.modern-tabs {
  border: none;
  background: white;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

.modern-tabs .nav-item {
  margin: 0 4px;
}

.modern-tabs .nav-link {
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  position: relative;
}

.modern-tabs .nav-link:hover {
  background: #f1f5f9;
  color: #3e5fce;
}

.modern-tabs .nav-link.active {
  background: #3e5fce;
  color: white;
  box-shadow: 0 4px 12px rgba(62, 95, 206, 0.4);
}

.badge-count {
  background: rgba(255, 255, 255, 0.3);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  margin-left: 8px;
  font-weight: 600;
}

.modern-tabs .nav-link.active .badge-count {
  background: rgba(255, 255, 255, 0.25);
}

/* Tab Content */
.modern-tab-content {
  padding: 0;
}

/* Content Card */
.content-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

/* Controls Section */
.controls-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
}

.controls-wrapper {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.control-label {
  font-weight: 600;
  color: #475569;
  font-size: 14px;
  margin: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.modern-select {
  min-width: 150px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  background: white;
}

.modern-select:focus {
  border-color: #3e5fce;
  box-shadow: 0 0 0 4px rgba(62, 95, 206, 0.1);
}

/* Modern Table */
.modern-table {
  margin: 0;
  border: none;
  border-collapse: separate;
  border-spacing: 0 8px;
}

.modern-table thead tr {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.modern-table thead th {
  border: none;
  padding: 16px;
  font-weight: 700;
  color: #334155;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: transparent;
}

.modern-table thead th:first-child {
  border-radius: 12px 0 0 12px;
}

.modern-table thead th:last-child {
  border-radius: 0 12px 12px 0;
}

.modern-table tbody tr {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  border-radius: 12px;
}

.modern-table tbody tr:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.modern-table tbody td {
  padding: 18px 16px;
  border: none;
  vertical-align: middle;
  font-size: 14px;
  color: #475569;
}

.modern-table tbody td:first-child {
  border-radius: 12px 0 0 12px;
}

.modern-table tbody td:last-child {
  border-radius: 0 12px 12px 0;
}

/* Badges */
.priority-badge, .status-badge, .type-badge, .date-badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.date-badge {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.priority-high {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #fecaca;
}

.priority-medium {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
  border: 1px solid #fde68a;
}

.priority-low {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #a7f3d0;
}

.status-pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
  border: 1px solid #fde68a;
}

.status-in-progress {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.status-scheduled {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4f46e5;
  border: 1px solid #c7d2fe;
}

.status-pending-closure {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  color: #c2410c;
  border: 1px solid #fdba74;
}

.status-completed {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #a7f3d0;
}

.type-badge {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4f46e5;
  border: 1px solid #c7d2fe;
}

.type-call {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.type-visit {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
  color: #be185d;
}

/* Buttons */
.btn-action {
  background: #3e5fce;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(62, 95, 206, 0.3);
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(62, 95, 206, 0.4);
  color: white;
}

.btn-action:disabled {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

/* Pagination */
.pagination-section {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 2px solid #f1f5f9;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-info-section {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.pagination-divider {
  color: #cbd5e1;
  margin: 0 4px;
}

.pagination-controls-section {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-pagination {
  background: white;
  border: 2px solid #e2e8f0;
  color: #475569;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-pagination:hover:not(:disabled) {
  background: #3e5fce;
  color: white;
  border-color: #3e5fce;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(62, 95, 206, 0.3);
}

.btn-pagination:disabled {
  background: #f8fafc;
  color: #cbd5e1;
  border-color: #f1f5f9;
  cursor: not-allowed;
}

.page-input-group {
  display: flex;
  align-items: center;
}

.page-input {
  width: 80px;
  text-align: center;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.page-input:focus {
  border-color: #3e5fce;
  box-shadow: 0 0 0 4px rgba(62, 95, 206, 0.1);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 80px;
  color: #cbd5e1;
  margin-bottom: 20px;
  display: block;
}

.empty-state h4 {
  color: #475569;
  font-weight: 700;
  margin-bottom: 12px;
}

.empty-state p {
  color: #94a3b8;
  font-size: 15px;
}

/* Modal */
.modern-modal .modal-header {
  background: #3e5fce;
  color: white;
  border: none;
  border-radius: 16px 16px 0 0;
  padding: 24px 28px;
}

.modern-modal .modal-title {
  font-weight: 700;
  font-size: 20px;
  display: flex;
  align-items: center;
}

.modern-modal .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.modern-modal .modal-body {
  padding: 28px;
}

.modern-modal .form-label {
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.modern-input {
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.3s ease;
}

.modern-input:focus {
  border-color: #3e5fce;
  box-shadow: 0 0 0 4px rgba(62, 95, 206, 0.1);
}

.modern-modal .modal-footer {
  border: none;
  padding: 20px 28px 28px;
  gap: 12px;
}

.btn-cancel {
  background: #f1f5f9;
  color: #64748b;
  border: 2px solid #e2e8f0;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
}

.btn-cancel:hover {
  background: #e2e8f0;
  color: #475569;
  border-color: #cbd5e1;
}

.btn-submit {
  background: #3e5fce;
  color: white;
  border: none;
  padding: 10px 28px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(62, 95, 206, 0.3);
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(62, 95, 206, 0.4);
}

.btn-submit:disabled {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

/* Loading Spinner */
.spinner-border-lg {
  width: 3rem;
  height: 3rem;
}

/* Responsive */
@media (max-width: 768px) {
  .update-works-container {
    padding: 16px;
  }

  .page-header {
    padding: 24px;
  }

  .page-title {
    font-size: 22px;
  }

  .content-card {
    padding: 20px;
  }

  .controls-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .control-group {
    flex-direction: column;
    align-items: stretch;
  }

  .modern-select {
    width: 100%;
  }

  .pagination-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-controls-section {
    justify-content: center;
  }

  .modern-table {
    font-size: 13px;
  }

  .modern-table thead th,
  .modern-table tbody td {
    padding: 12px 8px;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-row-hover {
  animation: fadeIn 0.3s ease;
}
</style>
